/**
 * Bliss Basic — standardized, lower-price line routed from custom funnel price objections.
 * Default: server webhook only (no redirect to a partner marketing site).
 * Optional: open a landing URL when VITE_BLISS_BASIC_OPEN_PARTNER_LANDING=true.
 */

export type EstimateExitSentiment = 'research' | 'questions' | 'price_high' | 'basic_inquiry'

export const BLISS_BASIC_BRAND = 'Bliss Basic'

export const BLISS_BASIC_TAGLINE =
  'Standardized bunk models with curated options — a lower investment than fully custom Bliss Bedrooms.'

export const BLISS_BASIC_HERO_EYEBROW = 'Same philosophy. A smarter approach.'

export const BLISS_BASIC_HERO_TITLE = 'Bliss Production, Budget Pricing'

/** Split headline for luxury two-line hero typography on basic.blissbunkbeds.com */
export const BLISS_BASIC_HERO_TITLE_LINES = {
  primary: 'Bliss Production',
  secondary: 'Budget Pricing',
} as const

export const BLISS_BASIC_HERO_BODY =
  'Bliss Basic brings the craftsmanship and thoughtful design Bliss is known for — with standardized layouts, curated finishes, and a simpler path from first call to installation.'

/** Entry price advertised on basic.blissbunkbeds.com. */
export const BLISS_BASIC_STARTING_PRICE_USD = 3_800

export const BLISS_BASIC_STARTING_PRICE_PREFIX = 'Starting at'

/** Anchor for Bliss Custom on package comparison (design + fabrication — not Bliss Basic). */
export const BLISS_CUSTOM_PACKAGE_COMPARE_USD = 15_000

export const BLISS_BASIC_HERO_LEAD = BLISS_BASIC_HERO_BODY

export type BlissBasicFeatureIcon = 'home' | 'layers' | 'gear' | 'hardhat' | 'heart'

export const BLISS_BASIC_FEATURE_ITEMS: ReadonlyArray<{
  title: string
  subtitle: string
  icon: BlissBasicFeatureIcon
}> = [
  {
    title: 'Standardized layouts',
    subtitle: 'Beautiful, proven designs',
    icon: 'home' as const,
  },
  {
    title: 'Curated finishes',
    subtitle: 'Timeless & durable',
    icon: 'layers' as const,
  },
  {
    title: 'Fewer custom selections',
    subtitle: 'A simplified process',
    icon: 'gear' as const,
  },
  {
    title: 'Professional installation',
    subtitle: 'Experts you can trust',
    icon: 'hardhat' as const,
  },
  {
    title: 'Designed for function and beauty',
    subtitle: 'Spaces your family will love',
    icon: 'heart',
  },
]

export type BlissBasicModelId =
  | 'twin_full'
  | 'full_full'
  | 'triple_stack'
  | 'quad_corner'
  | 'not_sure'

export type BlissBasicModelOption = {
  id: BlissBasicModelId
  label: string
  detail: string
  /** Indicative range for planning — final quote confirmed by team. */
  priceFromUsd: number
  priceToUsd: number
  sleeps: number
}

export const BLISS_BASIC_MODEL_OPTIONS: BlissBasicModelOption[] = [
  {
    id: 'twin_full',
    label: 'Twin over Full',
    detail: 'Classic family bunk — great for kids and guests.',
    priceFromUsd: 8_900,
    priceToUsd: 12_500,
    sleeps: 3,
  },
  {
    id: 'full_full',
    label: 'Full over Full',
    detail: 'More sleeping area for teens and vacation homes.',
    priceFromUsd: 10_500,
    priceToUsd: 14_800,
    sleeps: 4,
  },
  {
    id: 'triple_stack',
    label: 'Triple stack',
    detail: 'Three beds in one footprint for tight rooms.',
    priceFromUsd: 11_200,
    priceToUsd: 15_600,
    sleeps: 3,
  },
  {
    id: 'quad_corner',
    label: 'Corner quad layout',
    detail: 'L-shaped configuration for shared bunk rooms.',
    priceFromUsd: 14_000,
    priceToUsd: 19_500,
    sleeps: 4,
  },
  {
    id: 'not_sure',
    label: 'Not sure yet',
    detail: 'We will recommend a layout from your room size and sleep count.',
    priceFromUsd: 8_900,
    priceToUsd: 19_500,
    sleeps: 4,
  },
]

export const BLISS_BASIC_TIMELINE_OPTIONS = [
  { value: 'asap', label: 'ASAP' },
  { value: '1-3months', label: '1–3 months' },
  { value: '3-6months', label: '3–6 months' },
  { value: 'exploring', label: 'Just exploring' },
] as const

export type BlissBasicTimeline = (typeof BLISS_BASIC_TIMELINE_OPTIONS)[number]['value']

export function blissBasicModelById(id: BlissBasicModelId): BlissBasicModelOption {
  return BLISS_BASIC_MODEL_OPTIONS.find((m) => m.id === id) ?? BLISS_BASIC_MODEL_OPTIONS[0]
}

export function formatBlissBasicPriceRange(low: number, high: number): string {
  const fmt = (n: number) => `$${n.toLocaleString()}`
  return `${fmt(low)} – ${fmt(high)}`
}

export function formatBlissBasicUsd(usd: number): string {
  return `$${usd.toLocaleString()}`
}

export const ESTIMATE_EXIT_SENTIMENT_COPY: Record<
  EstimateExitSentiment,
  { title: string; subtitle: string }
> = {
  research: {
    title: 'Looks good — I just need to do a little more research',
    subtitle: 'We’ll email your estimate and helpful resources.',
  },
  questions: {
    title: 'Looks good — I have a few questions',
    subtitle: 'Talk with our team or start with a low-commitment consultation.',
  },
  price_high: {
    title: 'It’s more than I expected',
    subtitle: 'Explore Bliss Basic or step down to a smaller next step.',
  },
}

/** Your Bliss Basic marketing site (never the partner’s external domain). */
export function blissBasicSiteUrl(params?: {
  email?: string
  name?: string
  zip?: string
  quoteRequestId?: string
  /** Resume Bunkroom → Basic handoff with saved layout. */
  planId?: string
}): string {
  const base =
    (import.meta.env.VITE_BLISS_BASIC_LANDING_URL as string | undefined)?.trim() ||
    'https://basic.blissbunkbeds.com'
  if (!params) return base
  const q = new URLSearchParams()
  if (params.planId?.trim()) {
    q.set('plan_id', params.planId.trim())
    q.set('source', 'design')
  }
  if (params.email) q.set('email', params.email)
  if (params.name) q.set('name', params.name)
  if (params.zip) q.set('zip', params.zip)
  if (params.quoteRequestId) q.set('bliss_quote_id', params.quoteRequestId)
  q.set('utm_source', 'bliss_custom_funnel')
  q.set('utm_medium', 'price_objection')
  const qs = q.toString()
  return qs ? `${base.replace(/\?$/, '')}?${qs}` : base
}

/** @deprecated Use blissBasicSiteUrl — kept for callers that referenced the old name. */
export function blissBasicLandingUrl(params?: Parameters<typeof blissBasicSiteUrl>[0]): string {
  return blissBasicSiteUrl(params)
}

export const BLISS_BASIC_FUNNEL_REDIRECT_THANKS =
  'Taking you to Bliss Basic with your details prefilled…'
